import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProductGalleryComponent } from './product-gallery.component';
import { ProductBuyOptionsComponent } from './product-buy-options';
import { ProductDescriptionTabsComponent } from './product-description-tabs.component';
import { ProductRelatedSectionComponent } from './product-related-section.component';
import { ProductDetail, ProductAddon, AddToCartPayload, ProductStore } from '../../core/models/interfaces-general';
import { ProductoService } from '../../core/services/producto.service';
import { StockEstablecimientoApi } from '../../core/models/api/producto-api';
import { ProductoMapper } from '../../core/mappers/producto.mapper';
import { Constante } from '../../constante';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

@Component({
    selector: 'app-product-detail-page',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        ProductGalleryComponent,
        ProductBuyOptionsComponent,
        ProductDescriptionTabsComponent,
        ProductRelatedSectionComponent,
        UiLoadingComponent
    ],
    template: `
    <app-ui-loading *ngIf="!product()"></app-ui-loading>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-6 flex flex-col gap-10" *ngIf="product()">
      
      <nav class="text-xs text-gray-400 font-medium flex items-center gap-2">
        <a routerLink="/" class="hover:text-rose-500">Inicio</a> / 
        <a routerLink="/catalogo" class="hover:text-rose-500">Catálogo</a> / 
        <span class="text-gray-600 font-semibold">{{ product()!.title }}</span>
      </nav>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        <app-product-gallery 
          [images]="product()!.images" 
          [altText]="product()!.title"
        ></app-product-gallery>

        <div class="lg:sticky lg:top-6">
          <app-product-buy-options 
            [product]="product()!"
            [availableAddons]="addons()"
            (buy)="onAddToCart($event)"
          ></app-product-buy-options>
        </div>
      </div>

      <section class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div class="flex items-center gap-3 bg-white rounded-2xl border border-rose-100 p-4">
          <span class="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7h11v8H3zM14 10h4l3 3v2h-7zM7 20a2 2 0 100-4 2 2 0 000 4zm11 0a2 2 0 100-4 2 2 0 000 4z" /></svg>
          </span>
          <div>
            <p class="text-sm font-black text-gray-800">Entrega el mismo día</p>
            <p class="text-xs text-gray-500">Puno y alrededores</p>
          </div>
        </div>
        <div class="flex items-center gap-3 bg-white rounded-2xl border border-rose-100 p-4">
          <span class="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.6 2A9 9 0 1112 3a9 9 0 018.6 9z" /></svg>
          </span>
          <div>
            <p class="text-sm font-black text-gray-800">Pago seguro</p>
            <p class="text-xs text-gray-500">Yape y Plin</p>
          </div>
        </div>
        <div class="flex items-center gap-3 bg-white rounded-2xl border border-rose-100 p-4">
          <span class="w-11 h-11 rounded-2xl bg-rose-50 flex items-center justify-center shrink-0">
            <svg class="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h8m-8 4h5m-5-8h8M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </span>
          <div>
            <p class="text-sm font-black text-gray-800">Confirmación de entrega</p>
            <p class="text-xs text-gray-500">Te avisamos cuando llegue</p>
          </div>
        </div>
      </section>

      <section *ngIf="stockSucursales().length > 0" class="bg-white rounded-[2rem] border border-rose-100 p-5">
        <div class="flex items-center gap-2 mb-3">
          <svg class="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><circle cx="12" cy="11" r="3" /></svg>
          <p class="text-sm font-black text-gray-800">Disponible para recojo</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div *ngFor="let s of stockSucursales()" class="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
            <div class="min-w-0">
              <p class="text-sm font-bold text-gray-800 truncate">{{ s.nombre }}</p>
              <p class="text-[11px] text-gray-400">Listo para recoger</p>
            </div>
            <span class="text-xs font-black text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full shrink-0">{{ s.disponible }} disp.</span>
          </div>
        </div>
      </section>

      <app-product-description-tabs 
        [title]="product()!.title"
        [description]="product()!.descriptionHtml || ''"
        [specifications]="product()!.specifications || []"
      ></app-product-description-tabs>

      <app-product-related-section 
        [relatedProducts]="relatedProducts()"
      ></app-product-related-section>

    </main>
  `
})
export class ProductDetailPageComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private productoService = inject(ProductoService);

    product = signal<ProductDetail | null>(null);
    addons = signal<ProductAddon[]>([]);
    relatedProducts = signal<ProductStore[]>([]);
    stockSucursales = signal<StockEstablecimientoApi[]>([]);

    ngOnInit() {
        this.route.paramMap.subscribe((params) => {
            const id = params.get('id');
            if (!id) {
                this.router.navigate([Constante.URL_CATALOGO]);
                return;
            }
            this.load(id);
        });
    }

    onAddToCart(_payload: AddToCartPayload) {
        this.router.navigate([Constante.URL_CHECKOUT]);
    }

    private load(id: string) {
        this.productoService.getProducto(id).subscribe({
            next: (detalle) => {
                if (!detalle) {
                    this.router.navigate([Constante.URL_CATALOGO]);
                    return;
                }
                this.product.set(ProductoMapper.toDetail(detalle));
                this.addons.set((detalle.complementos ?? []).map((item) => ProductoMapper.toAddon(item)));
                this.relatedProducts.set((detalle.relacionados ?? []).map((item) => ProductoMapper.toStore(item)));
                this.stockSucursales.set(detalle.stockPorEstablecimiento ?? []);
            },
            error: () => {
                this.router.navigate([Constante.URL_CATALOGO]);
            }
        });
    }
}
